import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { formatDisplayDateTime } from '@/utils/Date';

export interface PDFExportOptions {
  filename: string;
  title: string;
  subtitle?: string;
  orientation?: "portrait" | "landscape";
  includeTimestamp?: boolean;
}

/**
 * Generate PDF from HTML element
 * Useful for exporting charts and tables
 */
export const generatePdfFromElement = async (
  element: HTMLElement,
  options: PDFExportOptions,
): Promise<void> => {
  try {
    const {
      filename,
      title,
      subtitle,
      orientation = "landscape",
      includeTimestamp = true,
    } = options;

    // Capture the element as canvas
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
    });

    const imgData = canvas.toDataURL("image/png");
    const imgWidth = orientation === "landscape" ? 297 : 210; // A4 dimensions in mm
    const pageHeight = orientation === "landscape" ? 210 : 297;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    const pdf = new jsPDF({
      orientation,
      unit: "mm",
      format: "a4",
    });

    let yPosition = 15;

    // Add title
    pdf.setFontSize(16);
    pdf.setTextColor(33, 33, 33); // Dark gray
    pdf.text(title, 15, yPosition);
    yPosition += 8;

    // Add subtitle if provided
    if (subtitle) {
      pdf.setFontSize(11);
      pdf.setTextColor(100, 100, 100); // Medium gray
      pdf.text(subtitle, 15, yPosition);
      yPosition += 6;
    }

    // Add timestamp
    if (includeTimestamp) {
      pdf.setFontSize(9);
      pdf.setTextColor(150, 150, 150); // Light gray
      const timestamp = formatDisplayDateTime(new Date());
      pdf.text(`Generated: ${timestamp}`, 15, yPosition);
      yPosition += 5;
    }

    // Add separator line
    pdf.setDrawColor(200, 200, 200);
    pdf.line(15, yPosition, imgWidth - 15, yPosition);
    yPosition += 5;

    // Add image
    const maxHeight = pageHeight - yPosition - 10;
    let finalHeight = imgHeight;
    if (finalHeight > maxHeight) {
      finalHeight = maxHeight;
    }

    pdf.addImage(imgData, "PNG", 15, yPosition, imgWidth - 30, finalHeight);

    // Add footer
    const pageCount = pdf.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.text(
        `Page ${i} of ${pageCount}`,
        pdf.internal.pageSize.getWidth() / 2,
        pdf.internal.pageSize.getHeight() - 8,
        { align: "center" },
      );
    }

    pdf.save(filename);
  } catch (error) {
    console.error("Error generating PDF:", error);
    throw new Error("Failed to generate PDF");
  }
};

/**
 * Generate PDF from table data with charts
 * More structured approach for reports
 */
export const generateReportPdf = async (options: {
  filename: string;
  title: string;
  subtitle?: string;
  chartElement?: HTMLElement;
  tableElement?: HTMLElement;
  summaryData?: Record<string, string | number>;
  orientation?: "portrait" | "landscape";
  tableData?: { headers: string[]; rows: (string | number)[][] };
}): Promise<void> => {
  try {
    const {
      filename,
      title,
      subtitle,
      chartElement,
      tableElement,
      summaryData,
      orientation = "landscape",
      tableData,
    } = options;

    const pdf = new jsPDF({
      orientation,
      unit: "mm",
      format: "a4",
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let yPosition = 15;

    // Header
    pdf.setFontSize(18);
    pdf.setTextColor(33, 33, 33);
    pdf.text(title, 15, yPosition);
    yPosition += 8;

    if (subtitle) {
      pdf.setFontSize(11);
      pdf.setTextColor(100, 100, 100);
      pdf.text(subtitle, 15, yPosition);
      yPosition += 6;
    }

    // Timestamp
    pdf.setFontSize(9);
    pdf.setTextColor(150, 150, 150);
    const timestamp = formatDisplayDateTime(new Date());
    pdf.text(`Generated: ${timestamp}`, 15, yPosition);
    yPosition += 5;

    // Separator
    pdf.setDrawColor(200, 200, 200);
    pdf.line(15, yPosition, pageWidth - 15, yPosition);
    yPosition += 8;

    // Summary data if provided
    if (summaryData && Object.keys(summaryData).length > 0) {
      pdf.setFontSize(10);
      pdf.setTextColor(33, 33, 33);
      pdf.text("Summary", 15, yPosition);
      yPosition += 5;

      pdf.setFontSize(9);
      const summaryEntries = Object.entries(summaryData);
      const colWidth = (pageWidth - 30) / Math.min(summaryEntries.length, 3);

      summaryEntries.forEach((entry, index) => {
        if (index > 0 && index % 3 === 0) {
          yPosition += 8;
        }
        const xPos = 15 + (index % 3) * colWidth;
        pdf.setTextColor(100, 100, 100);
        pdf.text(`${entry[0]}:`, xPos, yPosition);
        pdf.setTextColor(33, 33, 33);
        pdf.text(String(entry[1]), xPos, yPosition + 4);
      });

      yPosition += 12;
    }

    // Chart
    if (chartElement) {
      const canvas = await html2canvas(chartElement, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");
      const chartWidth = pageWidth - 30;
      const chartHeight = (canvas.height * chartWidth) / canvas.width;

      if (yPosition + chartHeight > pageHeight - 20) {
        pdf.addPage();
        yPosition = 15;
      }

      pdf.addImage(imgData, "PNG", 15, yPosition, chartWidth, chartHeight);
      yPosition += chartHeight + 8;
    }

    // Table
    if (tableData && tableData.headers?.length) {
      // Render data-driven table (full data, not only visible DOM)
      if (yPosition > pageHeight - 40) {
        pdf.addPage();
        yPosition = 15;
      }

      const colWidth = (pageWidth - 30) / tableData.headers.length;
      const rowHeight = 12;
      const tableX = 15;
      const tableWidth = colWidth * tableData.headers.length;

      // Header row (no background fill to avoid dark render artifacts)
      pdf.setFillColor(240, 240, 240);
      pdf.setTextColor(33, 33, 33);
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "bold");

      // Explicit white header background to avoid any dark artifacts from rendering
      pdf.setFillColor(255, 255, 255);
      pdf.rect(tableX, yPosition, tableWidth, rowHeight, "F");
      // No per-cell fill; draw text directly
      tableData.headers.forEach((header, index) => {
        const xPos = tableX + index * colWidth;
        pdf.text(String(header), xPos + 2, yPosition + 5, {
          maxWidth: colWidth - 4,
        });
      });

      // Draw header borders once to avoid double-stroking
      const headerTopY = yPosition;
      const headerBottomY = yPosition + rowHeight;
      pdf.setDrawColor(220, 220, 220);
      pdf.setLineWidth(0.2);
      // Top & bottom borders of header
      pdf.line(tableX, headerTopY, tableX + tableWidth, headerTopY);
      pdf.line(tableX, headerBottomY, tableX + tableWidth, headerBottomY);
      // Vertical borders of header
      for (let i = 0; i <= tableData.headers.length; i++) {
        const x = tableX + i * colWidth;
        pdf.line(x, headerTopY, x, headerBottomY);
      }

      yPosition = headerBottomY;

      // Data rows
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.setTextColor(50, 50, 50);

      tableData.rows.forEach((row) => {
        if (yPosition > pageHeight - 15) {
          pdf.addPage();
          yPosition = 15;

          // Repeat header on new page (no background fill)
          pdf.setFillColor(240, 240, 240);
          pdf.setTextColor(33, 33, 33);
          pdf.setFontSize(9);
          pdf.setFont("helvetica", "bold");
          // Explicit white header background for repeated header
          pdf.setFillColor(255, 255, 255);
          pdf.rect(tableX, yPosition, tableWidth, rowHeight, "F");
          tableData.headers.forEach((header, index) => {
            const xPos = tableX + index * colWidth;
            pdf.text(String(header), xPos + 2, yPosition + 5, {
              maxWidth: colWidth - 4,
            });
          });
          // Draw header borders on new page
          const newHeaderTopY = yPosition;
          const newHeaderBottomY = yPosition + rowHeight;
          pdf.setDrawColor(220, 220, 220);
          pdf.setLineWidth(0.2);
          pdf.line(tableX, newHeaderTopY, tableX + tableWidth, newHeaderTopY);
          pdf.line(
            tableX,
            newHeaderBottomY,
            tableX + tableWidth,
            newHeaderBottomY,
          );
          for (let i = 0; i <= tableData.headers.length; i++) {
            const x = tableX + i * colWidth;
            pdf.line(x, newHeaderTopY, x, newHeaderBottomY);
          }
          yPosition = newHeaderBottomY;
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(8);
          pdf.setTextColor(50, 50, 50);
        }

        // No alternating row fill to avoid any dark row artifacts

        // Text
        row.forEach((cell, index) => {
          const xPos = tableX + index * colWidth;
          pdf.text(String(cell), xPos + 2, yPosition + 5, {
            maxWidth: colWidth - 4,
          });
        });

        // Grid lines for this row (single stroke per boundary)
        pdf.setDrawColor(220, 220, 220);
        pdf.setLineWidth(0.2);
        const rowBottomY = yPosition + rowHeight;
        // Bottom horizontal line
        pdf.line(tableX, rowBottomY, tableX + tableWidth, rowBottomY);
        // Vertical lines at column boundaries for this row segment
        for (let i = 0; i <= tableData.headers.length; i++) {
          const x = tableX + i * colWidth;
          pdf.line(x, yPosition, x, rowBottomY);
        }

        yPosition += rowHeight;
      });
    } else if (tableElement) {
      // Fallback: capture visible table DOM (legacy behavior)
      if (yPosition > pageHeight - 40) {
        pdf.addPage();
        yPosition = 15;
      }

      const canvas = await html2canvas(tableElement, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        onclone: (clonedDoc) => {
          try {
            const root = clonedDoc.body;
            // Force light backgrounds for common header roles/selectors
            const headerSelectors = [
              "th",
              "thead th",
              "thead",
              '[role="columnheader"]',
              ".rdt-TableHead",
              ".rdt_TableHead",
            ];
            const nodes = root.querySelectorAll(headerSelectors.join(","));
            nodes.forEach((el) => {
              (el as HTMLElement).style.backgroundColor = "#f0f0f0";
              (el as HTMLElement).style.color = "#212121";
              (el as HTMLElement).style.borderColor = "#e0e0e0";
            });
          } catch {
            // no-op: fallback silently if selector fails
          }
        },
      });

      const imgData = canvas.toDataURL("image/png");
      const tableWidth = pageWidth - 30;
      const tableHeight = (canvas.height * tableWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 15, yPosition, tableWidth, tableHeight);
    }

    // Footer on all pages
    const pageCount = pdf.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 8, {
        align: "center",
      });
    }

    pdf.save(filename);
  } catch (error) {
    console.error("Error generating PDF:", error);
    throw new Error("Failed to generate PDF");
  }
};

/**
 * Generate simple text-based PDF with data table
 * Lightweight alternative for simple reports
 */
export const generateSimplePdf = (options: {
  filename: string;
  title: string;
  subtitle?: string;
  headers: string[];
  rows: (string | number)[][];
  orientation?: "portrait" | "landscape";
}): void => {
  try {
    const {
      filename,
      title,
      subtitle,
      headers,
      rows,
      orientation = "landscape",
    } = options;

    const pdf = new jsPDF({
      orientation,
      unit: "mm",
      format: "a4",
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let yPosition = 15;

    // Header
    pdf.setFontSize(16);
    pdf.setTextColor(33, 33, 33);
    pdf.text(title, 15, yPosition);
    yPosition += 8;

    if (subtitle) {
      pdf.setFontSize(11);
      pdf.setTextColor(100, 100, 100);
      pdf.text(subtitle, 15, yPosition);
      yPosition += 6;
    }

    // Timestamp
    pdf.setFontSize(9);
    pdf.setTextColor(150, 150, 150);
    const timestamp = formatDisplayDateTime(new Date());
    pdf.text(`Generated: ${timestamp}`, 15, yPosition);
    yPosition += 8;

    // Table
    const colWidth = (pageWidth - 30) / headers.length;
    const rowHeight = 7;

    // Header row
    pdf.setFillColor(240, 240, 240);
    pdf.setTextColor(33, 33, 33);
    pdf.setFontSize(9);
    pdf.setFont("bold");

    headers.forEach((header, index) => {
      const xPos = 15 + index * colWidth;
      pdf.rect(xPos, yPosition, colWidth, rowHeight, "F");
      pdf.text(header, xPos + 2, yPosition + 5, { maxWidth: colWidth - 4 });
    });

    yPosition += rowHeight;

    // Data rows
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(50, 50, 50);

    rows.forEach((row, rowIndex) => {
      if (yPosition > pageHeight - 15) {
        pdf.addPage();
        yPosition = 15;

        // Repeat header on new page
        pdf.setFillColor(240, 240, 240);
        pdf.setTextColor(33, 33, 33);
        pdf.setFontSize(9);
        pdf.setFont("helvetica", "bold");

        headers.forEach((header, index) => {
          const xPos = 15 + index * colWidth;
          pdf.rect(xPos, yPosition, colWidth, rowHeight, "F");
          pdf.text(header, xPos + 2, yPosition + 5, { maxWidth: colWidth - 4 });
        });

        yPosition += rowHeight;
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        pdf.setTextColor(50, 50, 50);
      }

      // Alternate row colors
      if (rowIndex % 2 === 0) {
        pdf.setFillColor(250, 250, 250);
        row.forEach((_, index) => {
          const xPos = 15 + index * colWidth;
          pdf.rect(xPos, yPosition, colWidth, rowHeight, "F");
        });
      }

      // Draw borders
      pdf.setDrawColor(220, 220, 220);
      row.forEach((cell, index) => {
        const xPos = 15 + index * colWidth;
        pdf.rect(xPos, yPosition, colWidth, rowHeight);
        pdf.text(String(cell), xPos + 2, yPosition + 5, {
          maxWidth: colWidth - 4,
        });
      });

      yPosition += rowHeight;
    });

    // Footer
    pdf.setFontSize(8);
    pdf.setTextColor(150, 150, 150);
    pdf.text(`Page 1 of 1`, pageWidth / 2, pageHeight - 8, { align: "center" });

    pdf.save(filename);
  } catch (error) {
    console.error("Error generating PDF:", error);
    throw new Error("Failed to generate PDF");
  }
};
